import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * MetricLoopCard — "the loop, in one chart."
 *
 * The expanded/hero view of a metric: an annotated trend that proves the
 * report→refine loop. Flat baseline → a vertical lever marker (a
 * `semantic_interventions` row) → the climb after it, with a goal line, a
 * shaded post-lever band, and before/after annotations. Hand-rolled SVG (like
 * the design mockup) so the annotations land exactly where they should — the
 * inline tiles use the lighter shared `ChartRenderer` primitive.
 */

export type LoopPoint = { x: string; y: number };

export type MetricLoopCardData = {
  title: string;
  subtitle?: string;
  /** Percent values, 0–100. */
  series: LoopPoint[];
  goal?: number;
  /** Single lever marker — back-compat for demo / story data. */
  marker?: { x: string; label: string; sublabel?: string };
  /** All intervention markers; each draws its own vertical lever line. */
  markers?: { x: string; label?: string; sublabel?: string }[];
  /** Headline change chip, e.g. "+28 pts after lever". */
  deltaLabel?: string;
  preAnnotation?: string;
  postAnnotation?: string;
  nCaption?: string;
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Plot geometry within a 820×300 viewBox.
const L = 64;
const R = 790;
const T = 36;
const B = 250;
const yAt = (v: number) => B - (v / 100) * (B - T);

/** Parse a date string at local noon so day-of-month never flips across timezones. */
const parse = (s: string): number =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : s).getTime();

/** Month-boundary ticks across the date range. */
const monthTicks = (t0: number, t1: number): { t: number; label: string }[] => {
  const ticks: { t: number; label: string }[] = [];
  const d = new Date(t0);
  d.setDate(1);
  if (d.getTime() < t0) d.setMonth(d.getMonth() + 1);
  while (d.getTime() <= t1) {
    ticks.push({ t: d.getTime(), label: MONTHS[d.getMonth()] });
    d.setMonth(d.getMonth() + 1);
  }
  return ticks;
};

export const MetricLoopCard: React.FC<{ data: MetricLoopCardData }> = ({ data }) => {
  const theme = useTheme();
  const { series, goal, deltaLabel, preAnnotation, postAnnotation, nCaption } = data;

  const times = series.map(p => parse(p.x));
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const xAt = (t: number) => (t1 === t0 ? L : L + ((t - t0) / (t1 - t0)) * (R - L));
  // Clamp into the plot so a marker dated after the last trend point still shows.
  const clampX = (px: number) => Math.max(L, Math.min(R, px));

  const line = theme.palette.primary.dark;
  const amber = theme.palette.warning.dark;
  const goalColor = theme.palette.primary.main;

  const poly = series
    .map((p, i) => `${xAt(times[i]).toFixed(1)},${yAt(p.y).toFixed(1)}`)
    .join(' ');
  const ticks = monthTicks(t0, t1);

  const markerList = data.markers ?? (data.marker ? [data.marker] : []);
  const markers = markerList
    .map(m => ({ ...m, px: clampX(xAt(parse(m.x))) }))
    .filter(m => Number.isFinite(m.px));
  const firstMarkerX = markers.length ? Math.min(...markers.map(m => m.px)) : null;
  const lastMarkerX = markers.length ? Math.max(...markers.map(m => m.px)) : null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
            {data.title}
          </Typography>
          {data.subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {data.subtitle}
            </Typography>
          )}
        </Box>
        {deltaLabel && (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.4,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.success.main, 0.14),
              color: 'success.dark',
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {deltaLabel}
          </Box>
        )}
      </Stack>

      <Box
        component="svg"
        viewBox="0 0 820 300"
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {/* gridlines + y labels */}
        {[0, 25, 50, 75, 100].map(v => (
          <g key={v}>
            <line x1={L} y1={yAt(v)} x2={R} y2={yAt(v)} stroke={theme.palette.grey[100]} />
            <text
              x={L - 8}
              y={yAt(v) + 4}
              textAnchor="end"
              fontFamily="Inter, sans-serif"
              fontSize="11"
              fill={theme.palette.text.secondary}
            >
              {v}%
            </text>
          </g>
        ))}
        <line x1={L} y1={T} x2={L} y2={B} stroke={theme.palette.grey[200]} />
        <line x1={L} y1={B} x2={R} y2={B} stroke={theme.palette.grey[300]} />

        {/* goal line */}
        {goal != null && (
          <>
            <line
              x1={L}
              y1={yAt(goal)}
              x2={R}
              y2={yAt(goal)}
              stroke={goalColor}
              strokeWidth={1.3}
              strokeDasharray="2 4"
              opacity={0.6}
            />
            <text
              x={R}
              y={yAt(goal) - 5}
              textAnchor="end"
              fontFamily="Inter, sans-serif"
              fontSize="10"
              fontWeight="700"
              fill={goalColor}
            >
              goal {goal}%
            </text>
          </>
        )}

        {/* shaded region after the most recent lever */}
        {lastMarkerX != null && (
          <rect
            x={lastMarkerX}
            y={T}
            width={R - lastMarkerX}
            height={B - T}
            fill={theme.palette.primary.main}
            opacity={0.05}
          />
        )}

        {/* trend */}
        <polyline
          points={poly}
          fill="none"
          stroke={line}
          strokeWidth={2.6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {series.map((p, i) => (
          <circle
            key={`${times[i]}`}
            cx={xAt(times[i])}
            cy={yAt(p.y)}
            r={i === series.length - 1 ? 4.6 : 3.4}
            fill={line}
          />
        ))}

        {/* lever markers — one vertical line per intervention */}
        {markers.map((m, i) => {
          const nearRight = m.px > L + (R - L) * 0.6;
          const labelX = m.px + (nearRight ? -6 : 6);
          const anchor = nearRight ? 'end' : 'start';
          // Give each marker its own label row so clustered (same-date) markers
          // never overprint each other — reads as a dated list of changes.
          const labelY = T + 14 + i * 13;
          return (
            <g key={`${m.x}-${i}`}>
              <line
                x1={m.px}
                y1={T}
                x2={m.px}
                y2={B}
                stroke={amber}
                strokeWidth={1.6}
                strokeDasharray="4 3"
              />
              <circle cx={m.px} cy={T} r={4} fill={amber} />
              {m.label && (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={anchor}
                  fontFamily="Inter, sans-serif"
                  fontSize="10.5"
                  fontWeight="700"
                  fill={amber}
                >
                  {m.label}
                </text>
              )}
              {m.sublabel && (
                <text
                  x={labelX}
                  y={labelY + 13}
                  textAnchor={anchor}
                  fontFamily="Inter, sans-serif"
                  fontSize="9.5"
                  fill={theme.palette.text.secondary}
                >
                  {m.sublabel}
                </text>
              )}
            </g>
          );
        })}

        {/* before / after annotations */}
        {preAnnotation && firstMarkerX != null && (
          <text
            x={(L + firstMarkerX) / 2}
            y={yAt(series[0].y) - 14}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="11"
            fontWeight="700"
            fill={theme.palette.text.secondary}
          >
            {preAnnotation}
          </text>
        )}
        {postAnnotation && (
          <text
            x={xAt(times[Math.max(0, series.length - 2)])}
            y={yAt(series[Math.max(0, series.length - 2)].y) - 14}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="11"
            fontWeight="700"
            fill={theme.palette.success.dark}
          >
            {postAnnotation}
          </text>
        )}

        {/* x labels */}
        {ticks.map(tk => (
          <text
            key={tk.label}
            x={xAt(tk.t)}
            y={B + 18}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="10.5"
            fill={theme.palette.text.secondary}
          >
            {tk.label}
          </text>
        ))}

        {/* caption */}
        {nCaption && (
          <text
            x={(L + R) / 2}
            y={B + 40}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="10"
            fill={theme.palette.text.disabled}
          >
            {nCaption}
          </text>
        )}
      </Box>
    </Box>
  );
};
